import { useCallback, useEffect, useState } from "react"
import { AgreementConfirmDialog } from "@/components/agreements/agreement-confirm-dialog"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { Button, Skeleton } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { formatMoneyWhole } from "@/lib/billing-format"
import {
  quoteFlowBulkRegenerate,
  startFlowBulkRegenerate,
  type FlowBulkQuote,
  type FlowBulkStart,
} from "@/lib/flow-api"
import type { FlowNodeRow } from "@/types/flow"

const KIND_ORDER = ["image", "video", "video_transition", "video_upscale", "video_depth", "chain", "shot"] as const

type Props = {
  open: boolean
  projectId: number
  flowId: number
  nodeIds: number[]
  onOpenChange: (open: boolean) => void
  onStarted: (nodes: FlowNodeRow[]) => void
}

export function FlowBulkRegenerateDialog({ open, projectId, flowId, nodeIds, onOpenChange, onStarted }: Props) {
  const { t } = useTranslation(["flow", "workspace"])
  const [quote, setQuote] = useState<FlowBulkQuote | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [consent, setConsent] = useState<FlowBulkStart | null>(null)
  const idsKey = nodeIds.join(",")

  useEffect(() => {
    if (!open) {
      setQuote(null)
      setError(null)
      setStarting(false)
      setConsent(null)
      return
    }
    let cancelled = false
    setQuote(null)
    setError(null)
    void quoteFlowBulkRegenerate(projectId, flowId, nodeIds)
      .then((next) => {
        if (!cancelled) setQuote(next)
      })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : t("bulk.quoteFailed"))
      })
    return () => {
      cancelled = true
    }
    // idsKey — стабильный список id на время открытой модалки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, flowId, idsKey, t])

  const parts = KIND_ORDER.flatMap((kind) => {
    const count = quote?.counts[kind] ?? 0
    if (count <= 0) return []
    return [t(`bulk.kinds.${kind}`, { count })]
  })
  const list = joinQuoted(parts, t("bulk.and"))
  const text =
    quote == null
      ? null
      : quote.estimable
        ? t("bulk.summary", { list, price: formatMoneyWhole(quote.total_microcents, quote.currency) })
        : t("bulk.summaryUnknown", { list })
  const canConfirm = quote != null && parts.length > 0 && !starting

  const start = useCallback(
    async (agreementVersion?: number) => {
      setStarting(true)
      setError(null)
      try {
        const result = await startFlowBulkRegenerate(projectId, flowId, nodeIds, agreementVersion)
        if (result.needs_consent) {
          setConsent(result)
          setStarting(false)
          return
        }
        onStarted(result.nodes ?? [])
        onOpenChange(false)
      } catch (reason: unknown) {
        setError(reason instanceof Error ? reason.message : t("bulk.startFailed"))
        setStarting(false)
      }
    },
    [flowId, nodeIds, onOpenChange, onStarted, projectId, t],
  )

  return (
    <>
      <WorkspaceDialog
        open={open}
        onOpenChange={onOpenChange}
        title={t("bulk.title")}
        closeAriaLabel={t("bulk.cancel")}
        size="sm"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("bulk.cancel")}
            </Button>
            <Button type="button" disabled={!canConfirm} onClick={() => void start()}>
              {t("bulk.confirm")}
            </Button>
          </>
        }
      >
        <div className="px-5 py-4" aria-busy={quote == null && error == null}>
          {error ? <p className="text-sm text-red-400">{error}</p> : null}
          {!error && text == null ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : null}
          {!error && text ? <p className="text-sm text-ui-text-secondary">{text}</p> : null}
        </div>
      </WorkspaceDialog>
      <AgreementConfirmDialog
        open={consent != null}
        onOpenChange={(next) => {
          if (!next) setConsent(null)
        }}
        agreementKey={consent?.agreement_key ?? "seedance_avatar_registration"}
        intro={t("seedance.videoConsentIntro", {
          ns: "workspace",
          names: consent?.owners?.map((owner) => owner.label).join(", ") ?? "",
        })}
        onConfirm={(version) => {
          setConsent(null)
          void start(version)
        }}
      />
    </>
  )
}

function joinQuoted(parts: string[], and: string): string {
  if (parts.length <= 1) return parts[0] ?? ""
  return `${parts.slice(0, -1).join(", ")} ${and} ${parts[parts.length - 1]}`
}
